/**
 * Meta (Facebook) Pixel helper — GDPR-compliant.
 *
 * The Pixel script is NEVER injected unless the user has explicitly opted in
 * via the cookie consent banner ("Marketing/Tracking" toggle).
 *
 * Public API:
 *   loadPixel()            → injects fbevents.js + fires PageView (idempotent)
 *   unloadPixel()          → removes <script> tag and revokes window.fbq
 *   trackEvent(name, data) → fbq('track', name, data) — only if loaded
 *   isLoaded()             → true if pixel is currently active in this tab
 */

const PIXEL_ID = '1594935244945272';
const SCRIPT_ID = 'meta-pixel-script';
const NOSCRIPT_ID = 'meta-pixel-noscript';
const SRC = 'https://connect.facebook.net/en_US/fbevents.js';

let _loaded = false;

export function isLoaded() { return _loaded; }

export function loadPixel() {
  if (typeof window === 'undefined') return;
  if (_loaded || window.fbq) { _loaded = true; return; }

  // Standard Meta Pixel bootstrap (the snippet you provided), wrapped so we
  // only execute it on consent.
  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v; t.id = SCRIPT_ID;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  }(window, document, 'script', SRC);
  /* eslint-enable */

  // <noscript> fallback for users without JS — required by Meta's docs.
  try {
    if (!document.getElementById(NOSCRIPT_ID)) {
      const ns = document.createElement('noscript');
      ns.id = NOSCRIPT_ID;
      const img = document.createElement('img');
      img.height = 1; img.width = 1;
      img.style.display = 'none';
      img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`;
      ns.appendChild(img);
      document.head.appendChild(ns);
    }
  } catch { /* ignore */ }

  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');
  _loaded = true;
}

export function unloadPixel() {
  try {
    document.getElementById(SCRIPT_ID)?.remove();
    document.getElementById(NOSCRIPT_ID)?.remove();
  } catch { /* ignore */ }
  try {
    if (window.fbq) {
      // Best-effort revoke: Meta doesn't expose a real "unload", but we can
      // null out the global so subsequent track() calls noop, and the helper
      // below also gates by _loaded.
      delete window.fbq;
      delete window._fbq;
    }
  } catch { /* ignore */ }
  _loaded = false;
}

/**
 * Fire a single Pixel event. Standard Meta event names work directly:
 *   PageView, Lead, CompleteRegistration, Subscribe, Purchase, ViewContent,
 *   InitiateCheckout, AddToCart, Search, Contact …
 *
 * Custom events use the `trackCustom` Meta API instead of `track`.
 */
export function trackEvent(name, data = {}, { custom = false } = {}) {
  // Gate exclusively on `window.fbq` because the module-level `_loaded`
  // flag can be false in a second instance of this module (webpack chunk
  // splitting + dynamic imports occasionally produce two copies of the
  // module — one for the eager MetaPixelManager, one for the lazy
  // `await import()` callers). `window.fbq` is global and authoritative.
  if (typeof window === 'undefined' || !window.fbq) return;
  try {
    window.fbq(custom ? 'trackCustom' : 'track', name, data);
  } catch { /* ignore */ }
}

export function trackPageView(path) {
  if (typeof window === 'undefined' || !window.fbq) return;
  try {
    window.fbq('track', 'PageView', path ? { page_path: path } : undefined);
  } catch { /* ignore */ }
}
